import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { BehaviorSubject, catchError, map, Observable, of, retry, startWith, tap } from 'rxjs';
import { StateService } from '../../shared/services/state.service';
import { ErrorHandlerService } from './error-handler.service';
import { WebSocketRequest, WebSocketResponse } from '../interfaces/WebSocketRequest';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private _errorService = inject(ErrorHandlerService);

  private readonly keyphraseSocketUrl = environment.requestUrls.keyPhrases.webSocketKeyphraseUrl;
  private readonly topicSocketUrl = environment.requestUrls.topics.webSocketTopicsUrl;

  private keyphraseSocketSubject!: WebSocketSubject<WebSocketResponse>;
  private topicSocketSubject!: WebSocketSubject<WebSocketResponse>;

  private connectionStatus$ = new BehaviorSubject<boolean>(false);
  private retryCount = 0;
  private readonly MAX_RETRIES = 3;
  

  public keyphraseWebSocket$!: Observable<WebSocketResponse>;
  public topicsWebSocket$!: Observable<WebSocketResponse>;

  constructor() {
    this.initWebSocketConnection();

    this.keyphraseWebSocket$ = this.keyphraseSocketSubject.asObservable().pipe(
      retry(3),
      catchError((err) => {
        console.error('Web Socket Errored in Web Socket Service:', err);

        const appError = this._errorService.handleError(err);

        return of({ refresh: false });
      }),
      tap((data) => console.info('received something from socket', data))
    );

    this.topicsWebSocket$ = this.topicSocketSubject.asObservable().pipe(
      retry(3),
      catchError((err) => {
        console.error('Web Socket Errored in Web Socket Service:', err);

        const appError = this._errorService.handleError(err);

        return of({ refresh: false });
      })
    );
  }

  initWebSocketConnection() {
    this.keyphraseSocketSubject = webSocket({
      url: this.keyphraseSocketUrl,
      deserializer: ({ data }) => JSON.parse(data),
      serializer: (stringValue) => JSON.stringify(stringValue),
      openObserver: {
        next: () => {
          console.log('WebSocket Connected');
          this.connectionStatus$.next(true);
          this.retryCount = 0;
        }
      },
      closeObserver: {
        next: (event) => {
          console.log('WebSocket Closed:', event);
          this.connectionStatus$.next(false);
          this.handleReconnect();
        }
      }
    });

    this.topicSocketSubject = webSocket({
      url: this.topicSocketUrl,
      deserializer: ({ data }) => JSON.parse(data),
      serializer: (stringValue) => JSON.stringify(stringValue),
      openObserver: {
        next: () => {
          console.log('WebSocket Connected');
          this.connectionStatus$.next(true);
          this.retryCount = 0;
        }
      },
      closeObserver: {
        next: (event) => {
          console.log('WebSocket Closed:', event);
          this.connectionStatus$.next(false);
          this.handleReconnect();
        }
      }
    });
  }

  private handleReconnect() {
    if (this.retryCount < this.MAX_RETRIES) {
      this.retryCount++;
      console.log(`Attempting reconnect ${this.retryCount}/${this.MAX_RETRIES}`);
      setTimeout(() => this.initWebSocketConnection(), 1000 * this.retryCount);
    }
  }

  establishTopicsWebSocketConnection() {
    return this.topicsWebSocket$;
  }

  establishKeyphraseWebSocketConnection() {
    return this.keyphraseWebSocket$;
  }

  
}
